import { formatDistanceToNow } from 'date-fns';
import { FileText, CheckCircle2, AlertCircle, RefreshCw, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface ImportStatusCardProps {
  fileName?: string;
  fileSize?: number;
  uploadedAt?: string;
  totalRows: number;
  importedRows: number;
  skippedRows: number;
  warningRows: number;
  errorRows: number;
  onViewErrors?: () => void;
  onReupload?: () => void;
}

/**
 * ImportStatusCard - Compact status display for 100% screen fit
 */
export default function ImportStatusCard({
  fileName,
  fileSize,
  uploadedAt,
  totalRows,
  importedRows,
  skippedRows,
  warningRows,
  errorRows,
  onViewErrors,
  onReupload,
}: ImportStatusCardProps) {
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const successRate = totalRows > 0 ? Math.round((importedRows / totalRows) * 100) : 0;
  const hasIssues = warningRows > 0 || errorRows > 0;

  return (
    <div className="px-3 py-2 bg-card border-b border-border">
      <div className="flex items-center justify-between gap-4">
        {/* File Info */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded bg-primary/10 flex items-center justify-center shrink-0">
            <FileText className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium truncate">{fileName || 'No file'}</p>
              {fileSize && (
                <Badge variant="secondary" className="text-[10px] h-4 px-1">
                  {formatFileSize(fileSize)}
                </Badge>
              )}
            </div>
            {uploadedAt && (
              <p className="text-[10px] text-muted-foreground">
                {formatDistanceToNow(new Date(uploadedAt), { addSuffix: true })}
              </p>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            <span className="font-medium">{importedRows}</span>
            <span className="text-muted-foreground">imported</span>
          </div>
          {hasIssues && (
            <div className="flex items-center gap-1 text-amber-600">
              <AlertCircle className="h-3.5 w-3.5" />
              <span className="font-medium">{warningRows + errorRows}</span>
              <span>issues</span>
            </div>
          )}
          <span className="text-muted-foreground">
            {successRate}% success
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-1.5">
          {hasIssues && onViewErrors && (
            <Button variant="outline" size="sm" onClick={onViewErrors} className="h-6 text-[10px] px-2 gap-1">
              <Eye className="h-3 w-3" />
              Errors
            </Button>
          )}
          {onReupload && (
            <Button variant="ghost" size="sm" onClick={onReupload} className="h-6 text-[10px] px-2 gap-1">
              <RefreshCw className="h-3 w-3" />
              Re-upload
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
